import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaCheckCircle, FaMapMarkerAlt, FaQrcode, FaQuestionCircle, FaSyncAlt } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import HeaderCard from '../../components/Header/HeaderCard';
import TableList from '../../components/Tools/TableList';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';

/**
 * Présence du jour (contrat C12, Yros/Kavero#60).
 *
 * Écran consommateur : aucun modèle persisté, aucune `ir.rule`, aucun bridge
 * — la story ne change pas de nature. La donnée est déjà ouverte à tout
 * compte interne côté proxy (review 029, § « trou du rôle user ») : la garde
 * de route et l'entrée de menu sont du confort d'interface, pas une
 * autorisation.
 *
 * Au montage, puis sur demande via le bouton de rafraîchissement (constat en
 * conditions réelles, PR #304) — jamais de polling, une cadence serait une
 * décision que personne n'a prise. Toujours sans argument : Odoo calcule la
 * fenêtre du jour dans le fuseau du demandeur (`res_users.py:390-394`).
 * Composer une date côté navigateur rouvrirait le piège `toISOString()` de la
 * review 029.
 */

// Les trois états de contrôle de zone, jamais un booléen : couleur ET icône
// distinctes pour chacun — la distinction ne tient jamais à la couleur
// seule (un daltonien doit lire l'écart entre « hors zone » et
// « non vérifiable »).
const CONTROLES = {
  dans_zone: { couleur: 'bg-green-100 text-green-800', Icone: FaCheckCircle },
  hors_zone: { couleur: 'bg-red-100 text-red-800', Icone: FaMapMarkerAlt },
  non_verifiable: { couleur: 'bg-yellow-100 text-yellow-800', Icone: FaQuestionCircle },
};
const COULEUR_CONTROLE_DEFAUT = 'bg-gray-100 text-gray-800';

const EN_DEFAUT = ['hors_zone', 'non_verifiable'];
const FILTRES = ['tous', 'pas_pointe', 'defaut', 'hors_zone', 'non_verifiable'];

// `controle_entree` OU `controle_sortie` porte `etat` : jamais `&&`. Beta
// porte `hors_zone` en entrée ET `non_verifiable` en sortie sur la même
// ligne — elle doit répondre aux deux filtres séparément, c'est la raison
// d'être de la story.
const porte = (ligne, etat) => ligne.controle_entree === etat || ligne.controle_sortie === etat;

const REPOND_AU_FILTRE = {
  tous: () => true,
  pas_pointe: (ligne) => ligne.etat === 'pas_pointe',
  // Aucune garde sur `etat` : une ligne `pas_pointe` a ses deux contrôles à
  // `null` et n'y tombe pas d'elle-même — l'exclure explicitement déciderait
  // un état à la place d'Odoo.
  defaut: (ligne) => EN_DEFAUT.some((etat) => porte(ligne, etat)),
  hors_zone: (ligne) => porte(ligne, 'hors_zone'),
  non_verifiable: (ligne) => porte(ligne, 'non_verifiable'),
};

// `null` n'est pas `non_verifiable` : un contrôle nul (compris le mode
// `desactive`) ne doit ressembler à aucun défaut, jamais un `new Date(null)`.
function formaterHeure(iso, dateLocale) {
  if (!iso) {
    return '-';
  }
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) {
    return '-';
  }
  return date.toLocaleTimeString(dateLocale, { hour: '2-digit', minute: '2-digit' });
}

// Badge de contrôle : une information, un seul endroit — la cellule de
// l'heure qu'il qualifie. Contrôle nul → aucun badge. Code inconnu du
// catalogue → code brut affiché, sans icône ni couleur dédiée.
function BadgeControle({ code, t }) {
  if (!code) {
    return null;
  }
  const info = CONTROLES[code];
  const Icone = info?.Icone;
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
        info ? info.couleur : COULEUR_CONTROLE_DEFAUT
      }`}
    >
      {Icone && <Icone size={10} />}
      {t(`presenceJour.controles.${code}`, { defaultValue: code })}
    </span>
  );
}

// Écart entre la position reçue et le lieu affecté (C12 étendu,
// Yros/Kavero#463) : en mètres sous le kilomètre, en kilomètres au-delà, avec
// une décimale tant qu'elle dit quelque chose.
function formaterDistance(metres, langue) {
  if (metres < 1000) {
    return `${Math.round(metres)} m`;
  }
  const km = metres / 1000;
  return `${new Intl.NumberFormat(langue || 'fr', { maximumFractionDigits: km < 10 ? 1 : 0 }).format(km)} km`;
}

// Par quoi la présence a été établie, quand ce n'est pas le GPS. Un pointage
// par QR vaut `dans_zone` même si le téléphone se disait ailleurs : l'écart
// est alors montré à la RH, qui juge. Il ne l'est que si la position sort du
// rayon du lieu — une zone non configurée (rayon nul) ne contient rien, donc
// toute position connue y compte comme un écart. Un pointage par GPS n'affiche
// rien ici : son état de zone dit déjà tout.
function MethodeQr({ ligne, sens, t, langue }) {
  if (ligne[`methode_${sens}`] !== 'qr') {
    return null;
  }
  const distance = ligne[`distance_${sens}_m`];
  const horsRayon = Number.isFinite(distance) && !(distance <= ligne.rayon_m);
  return (
    <span className="inline-flex flex-wrap items-center gap-1 text-xs text-slate-600">
      <span
        data-testid={`presence-qr-${sens}-${ligne.employe_id}`}
        role="img"
        aria-label={t('presenceJour.parQr')}
        title={t('presenceJour.parQr')}
        className="inline-flex items-center"
      >
        <FaQrcode size={12} aria-hidden="true" />
      </span>
      {horsRayon && (
        <span data-testid={`presence-ecart-${sens}-${ligne.employe_id}`} className="text-amber-700">
          {t('presenceJour.ecartPosition', { distance: formaterDistance(distance, langue) })}
        </span>
      )}
    </span>
  );
}

// Refus métier de C12. Le statut d'abord, jamais la présence d'un message :
// une panne porte elle aussi un corps, mais dans `error`, et le servir tel quel
// mettrait un texte de proxy sous les yeux de la RH.
const REPLI_PRESENCE_PAR_MOTIF = {
  non_autorise: 'presenceJour.refusNonAutorise',
};
const interpreterProbleme = (erreur, t) => {
  const enveloppe = erreur?.response?.status === 409 ? erreur.response.data : null;
  if (!enveloppe) return t('presenceJour.erreurGenerique');
  const cleDeRepli = REPLI_PRESENCE_PAR_MOTIF[enveloppe.motif];
  return enveloppe.message || (cleDeRepli ? t(cleDeRepli) : t('presenceJour.erreurGenerique'));
};

function PresenceDuJour() {
  const { t, i18n } = useTranslation();
  // Même dérivation que MesDemandes.js:63.
  const dateLocale = i18n.resolvedLanguage?.startsWith('en') ? 'en-US' : 'fr-FR';

  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const [filtreActif, setFiltreActif] = useState('tous');
  const [recherche, setRecherche] = useState('');

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const reponse = await apiService.getPresenceDuJour();
      setDonnees(reponse || { date: null, lignes: [] });
    } catch (e) {
      setDonnees(null);
      // Un refus de capacité n'est pas une panne : dire « le service est
      // indisponible » envoyait la RH chercher du côté de l'infrastructure
      // pour un problème de droits (Yros/Kavero#372).
      setErreur(interpreterProbleme(e, t));
    } finally {
      setChargement(false);
    }
  }, [t]);

  useEffect(() => {
    charger();
  }, [charger]);

  // Rafraîchissement manuel (constat en conditions réelles, PR #304) : à la
  // différence de `charger`, ne touche ni `chargement` ni `erreur`. La photo
  // précédente reste affichée telle quelle pendant l'appel — la vider avant
  // la réponse ferait clignoter le tableau et, pire, affirmerait fugitivement
  // que personne n'a pointé. Un échec ne la remplace par rien non plus :
  // l'état d'erreur pleine page reste réservé au cas où rien n'a jamais pu
  // charger, jamais à un second appel qui échoue après un premier réussi.
  //
  // Séquencement (review de troisième passage) : sans lui, c'est la dernière
  // réponse ARRIVÉE qui écrit, pas la dernière PARTIE — un bouton sans
  // aucun retour invite à recliquer, et une réponse lente peut alors écraser
  // une réponse plus récente déjà affichée. `dernierAppelRef` retient le
  // numéro du dernier appel lancé ; chaque appel compare son propre numéro
  // à ce compteur à son réveil et sort avant d'écrire s'il a été supplanté
  // entre-temps. `enCours` double la garde par un retour visuel : c'est lui
  // qui rend un reclic improbable en pratique, le séquencement est la
  // garantie qui tient même si un second appel part autrement que par clic.
  const dernierAppelRef = useRef(0);
  const [enCours, setEnCours] = useState(false);

  const rafraichir = useCallback(async () => {
    const appelActuel = ++dernierAppelRef.current;
    setEnCours(true);
    try {
      const reponse = await apiService.getPresenceDuJour();
      if (appelActuel !== dernierAppelRef.current) {
        return;
      }
      setDonnees(reponse || { date: null, lignes: [] });
    } catch (e) {
      // Silencieux côté état : la donnée déjà affichée reste la meilleure
      // information disponible, la perdre serait pire que ne pas rafraîchir.
    } finally {
      if (appelActuel === dernierAppelRef.current) {
        setEnCours(false);
      }
    }
  }, []);

  const lignes = donnees?.lignes || [];

  const lignesAffichees = useMemo(() => {
    const predicat = REPOND_AU_FILTRE[filtreActif] || REPOND_AU_FILTRE.tous;
    // `setSearchQuery` de TableList rend un tableau de termes (un badge par
    // terme validé) : on ne retient que le premier, cet écran ne propose
    // qu'une recherche simple.
    const brut = Array.isArray(recherche) ? recherche[0] : recherche;
    const terme = (brut || '').trim().toLowerCase();
    return lignes
      .filter(predicat)
      .filter((ligne) => !terme || (ligne.nom || '').toLowerCase().includes(terme));
  }, [lignes, filtreActif, recherche]);

  const colonnes = useMemo(
    () => [
      {
        key: 'nom',
        label: t('presenceJour.colNom'),
        render: (ligne) => (
          <span data-testid={`presence-ligne-${ligne.employe_id}`}>{ligne.nom}</span>
        ),
      },
      {
        key: 'etat',
        label: t('presenceJour.colEtat'),
        render: (ligne) => t(`presenceJour.etats.${ligne.etat}`, { defaultValue: ligne.etat }),
      },
      {
        key: 'heure_entree',
        label: t('presenceJour.colEntree'),
        render: (ligne) => (
          <div className="flex flex-col gap-1">
            {/* Séparateur explicite : deux `<span>` adjacents sans texte
                entre eux se recollent dans le `textContent` du DOM (« Invalid
                DateHors zone »), la mise en page flex n'y change rien. */}
            <span>{formaterHeure(ligne.heure_entree, dateLocale)}</span>{' '}
            <BadgeControle code={ligne.controle_entree} t={t} />
            <MethodeQr ligne={ligne} sens="entree" t={t} langue={dateLocale} />
          </div>
        ),
      },
      {
        key: 'heure_sortie',
        label: t('presenceJour.colSortie'),
        render: (ligne) => (
          <div className="flex flex-col gap-1">
            <span>{formaterHeure(ligne.heure_sortie, dateLocale)}</span>{' '}
            <BadgeControle code={ligne.controle_sortie} t={t} />
            <MethodeQr ligne={ligne} sens="sortie" t={t} langue={dateLocale} />
          </div>
        ),
      },
    ],
    [t, dateLocale]
  );

  // Au-dessus du tableau (`toolbarActions` de TableList), avec les filtres :
  // cliquables même quand le filtre actif ne laisse aucune ligne, sinon la RH
  // se retrouve coincée devant un écran vide sans moyen de le rafraîchir.
  const barreOutils = (
    <>
      {FILTRES.map((cle) => (
        <button
          key={cle}
          type="button"
          data-testid={`presence-filtre-${cle}`}
          onClick={() => setFiltreActif(cle)}
          className={`px-3 py-1.5 text-sm font-medium rounded-full border transition-colors duration-200 ${
            filtreActif === cle
              ? 'bg-[--color-primary] text-white border-[--color-primary]'
              : 'bg-white text-[--color-primary] border-[--color-primary] hover:bg-[--color-primary] hover:text-white'
          }`}
        >
          {t(`presenceJour.filtres.${cle}`)}
        </button>
      ))}
      <button
        type="button"
        data-testid="presence-rafraichir"
        onClick={rafraichir}
        disabled={enCours}
        className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-full border transition-colors duration-200 bg-white text-[--color-primary] border-[--color-primary] hover:bg-[--color-primary] hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <FaSyncAlt size={12} />
        {t('presenceJour.rafraichir')}
      </button>
    </>
  );

  return (
    <div className="p-4">
      <HeaderCard
        title={t('presenceJour.titre')}
        text={donnees?.date ? t('presenceJour.sousTitre', { date: donnees.date }) : ''}
      />

      {erreur && (
        <p
          data-testid="presence-erreur"
          className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3"
        >
          {erreur}
        </p>
      )}

      {chargement ? (
        <LoadingBarWithStyles />
      ) : (
        !erreur && (
          <TableList
            columns={colonnes}
            data={lignesAffichees}
            searchQuery={recherche}
            setSearchQuery={setRecherche}
            showFilter={false}
            toolbarActions={barreOutils}
          />
        )
      )}
    </div>
  );
}

export default PresenceDuJour;
